import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@monbits/ui";
import { ArrowUpRight, Info, Landmark, RefreshCw } from "lucide-react";
import type * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import { BLOCKCHAIN_NAMES } from "src/constants/blockchains";
import type { TreasuryPosition } from "src/providers/treasury-provider";
import { useTreasurySummary } from "src/queries/treasury-queries";
import { formatCryptoAmount, splitTicker } from "src/utils/format";

const formatUsd = (value: string | undefined): string | null => {
	if (value === undefined) return null;
	const n = Number(value);
	if (Number.isNaN(n)) return null;
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(n);
};

const formatRelativeTime = (ts: number): string => {
	if (!ts) return "";
	const seconds = Math.floor((Date.now() - ts) / 1000);
	if (seconds < 10) return "just now";
	if (seconds < 60) return `${seconds}s ago`;
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	return `${hours}h ago`;
};

const TreasuryPage: React.FC = () => {
	const navigate = useNavigate();
	const { data, isLoading, isError, isFetching, dataUpdatedAt, refetch } =
		useTreasurySummary();

	const positions = data?.positions ?? [];
	const lastUpdated = formatRelativeTime(dataUpdatedAt);

	return (
		<div className="p-4 sm:p-6 space-y-6">
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div className="max-w-2xl">
					<h1 className="text-3xl font-bold">Treasury</h1>
					<p className="text-sm text-muted-foreground mt-1">
						Platform funds held across outbound wallets, what's owed to
						merchants, and what's safe to withdraw. Withdrawable is the surplus
						that isn't owed to merchants or already in flight; it is{" "}
						<Link to="/revenue" className="underline underline-offset-2">
							not the same as net profit
						</Link>
						.
					</p>
				</div>
				<div className="flex items-center gap-3 shrink-0">
					{lastUpdated && (
						<span className="text-xs text-muted-foreground tabular-nums">
							Updated {lastUpdated}
						</span>
					)}
					<Button
						variant="ghost"
						size="icon"
						onClick={() => refetch()}
						disabled={isFetching}
						aria-label="Refresh treasury data"
					>
						<RefreshCw
							className={isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"}
						/>
					</Button>
				</div>
			</div>

			{isLoading ? (
				<TreasurySkeleton />
			) : isError ? (
				<Card>
					<CardContent className="py-12 text-center space-y-3">
						<p className="text-sm text-muted-foreground">
							Couldn't load treasury data.
						</p>
						<Button variant="outline" size="sm" onClick={() => refetch()}>
							Retry
						</Button>
					</CardContent>
				</Card>
			) : positions.length === 0 ? (
				<EmptyTreasury />
			) : (
				<>
					<SummaryHero
						withdrawable={data?.totalWithdrawableUsd}
						held={data?.totalHeldUsd}
						owed={data?.totalOwedUsd}
					/>

					<Card>
						<CardHeader className="pb-2">
							<CardTitle className="text-base font-semibold">
								By currency
							</CardTitle>
							<CardDescription className="text-xs">
								Crypto amounts are authoritative; USD is an indicative roll-up.
							</CardDescription>
						</CardHeader>
						<CardContent className="px-0 sm:px-2">
							<div className="overflow-x-auto">
								<TooltipProvider delayDuration={150}>
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Currency</TableHead>
												<TableHead className="text-right">Held</TableHead>
												<TableHead className="text-right">
													Owed to merchants
												</TableHead>
												<TableHead className="text-right">
													<Tooltip>
														<TooltipTrigger className="inline-flex items-center gap-1 cursor-help">
															In flight
															<Info className="h-3 w-3 text-muted-foreground" />
														</TooltipTrigger>
														<TooltipContent className="max-w-xs">
															Funds already committed to pool withdrawals but
															not yet settled on-chain. Subtracted from
															withdrawable.
														</TooltipContent>
													</Tooltip>
												</TableHead>
												<TableHead className="text-right">
													Withdrawable
												</TableHead>
												<TableHead className="w-[140px]" />
											</TableRow>
										</TableHeader>
										<TableBody>
											{positions.map((p) => (
												<TreasuryRow
													key={`${p.currency}-${p.networkId}`}
													position={p}
													onViewWallets={() =>
														navigate(
															`/wallets/outbound?blockchain=${p.blockchain}`,
														)
													}
												/>
											))}
										</TableBody>
									</Table>
								</TooltipProvider>
							</div>
						</CardContent>
					</Card>
				</>
			)}
		</div>
	);
};

interface SummaryHeroProps {
	withdrawable: string | undefined;
	held: string | undefined;
	owed: string | undefined;
}

const SummaryHero: React.FC<SummaryHeroProps> = ({
	withdrawable,
	held,
	owed,
}) => {
	const negative = withdrawable !== undefined && Number(withdrawable) < 0;

	return (
		<div className="stagger-in rounded-xl border bg-card p-6 sm:p-8">
			<div className="flex items-center gap-2 text-muted-foreground">
				<Landmark className="size-4" />
				<p className="text-sm font-medium">Withdrawable (all currencies)</p>
			</div>
			<p
				className={`text-4xl sm:text-5xl font-bold tracking-tight tabular-nums mt-3${
					negative ? " text-[var(--trend-negative)]" : ""
				}`}
			>
				{formatUsd(withdrawable) ?? "$0.00"}
			</p>
			<p className="text-xs text-muted-foreground mt-2">
				Indicative USD roll-up. Safe to withdraw without touching merchant-owed
				funds.
			</p>

			<div className="mt-6 pt-6 border-t grid grid-cols-1 sm:grid-cols-2 gap-6">
				<div>
					<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
						Held in wallets
					</p>
					<p className="text-2xl font-semibold tracking-tight tabular-nums mt-1.5">
						{formatUsd(held) ?? "$0.00"}
					</p>
				</div>
				<div>
					<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
						Owed to merchants
					</p>
					<p className="text-2xl font-semibold tracking-tight tabular-nums mt-1.5">
						{formatUsd(owed) ?? "$0.00"}
					</p>
				</div>
			</div>
		</div>
	);
};

interface TreasuryRowProps {
	position: TreasuryPosition;
	onViewWallets: () => void;
}

const TreasuryRow: React.FC<TreasuryRowProps> = ({
	position,
	onViewWallets,
}) => {
	const negative = Number(position.withdrawable) < 0;
	const { token, blockchain } = splitTicker(position.currency);
	const networkName =
		BLOCKCHAIN_NAMES[position.blockchain] ?? position.blockchain;
	const amountOpts = { ticker: position.currency, maxDecimals: 8 };
	const heldUsd = formatUsd(position.heldUsd);
	const owedUsd = formatUsd(position.owedUsd);
	const inFlightUsd = formatUsd(position.inFlightUsd);
	const withdrawableUsd = formatUsd(position.withdrawableUsd);

	return (
		<TableRow>
			<TableCell>
				<div className="flex items-center gap-2">
					<CryptoIcon
						ticker={position.currency}
						showComposite
						className="h-5 w-5"
					/>
					<div className="leading-tight">
						<div className="text-sm font-medium">{token || blockchain}</div>
						<div className="text-xs text-muted-foreground">{networkName}</div>
					</div>
				</div>
			</TableCell>
			<TableCell className="text-right font-mono tabular-nums text-sm">
				{formatCryptoAmount(position.held, amountOpts)}
				{heldUsd && (
					<div className="text-xs text-muted-foreground">{heldUsd}</div>
				)}
			</TableCell>
			<TableCell className="text-right font-mono tabular-nums text-sm">
				{formatCryptoAmount(position.owed, amountOpts)}
				{owedUsd && (
					<div className="text-xs text-muted-foreground">{owedUsd}</div>
				)}
			</TableCell>
			<TableCell className="text-right font-mono tabular-nums text-sm text-muted-foreground">
				{formatCryptoAmount(position.inFlight, amountOpts)}
				{inFlightUsd && <div className="text-xs">{inFlightUsd}</div>}
			</TableCell>
			<TableCell
				className={`text-right font-mono tabular-nums text-sm font-semibold${
					negative ? " text-[var(--trend-negative)]" : ""
				}`}
			>
				{formatCryptoAmount(position.withdrawable, amountOpts)}
				{withdrawableUsd && (
					<div
						className={`text-xs font-normal${
							negative ? "" : " text-muted-foreground"
						}`}
					>
						{withdrawableUsd}
					</div>
				)}
			</TableCell>
			<TableCell className="text-right">
				<Button
					size="sm"
					variant="ghost"
					onClick={onViewWallets}
					className="text-muted-foreground hover:text-foreground"
				>
					View wallets
					<ArrowUpRight className="ml-1.5 h-3.5 w-3.5" />
				</Button>
			</TableCell>
		</TableRow>
	);
};

const EmptyTreasury: React.FC = () => (
	<Card>
		<CardContent className="flex flex-col items-center justify-center py-16 text-center">
			<Landmark className="h-10 w-10 text-muted-foreground mb-4" />
			<h3 className="text-lg font-semibold">No withdrawable funds</h3>
			<p className="text-sm text-muted-foreground mt-1 max-w-md">
				The platform holds no surplus right now. All wallet funds are either
				owed to merchants or already in flight.
			</p>
		</CardContent>
	</Card>
);

const TreasurySkeleton: React.FC = () => (
	<div className="space-y-6">
		<div className="rounded-xl border bg-card p-6 sm:p-8 space-y-4">
			<Skeleton className="h-4 w-40" />
			<Skeleton className="h-12 w-64" />
			<div className="grid grid-cols-2 gap-6 pt-6 border-t">
				<Skeleton className="h-12 w-full" />
				<Skeleton className="h-12 w-full" />
			</div>
		</div>
		<Card>
			<CardHeader className="pb-2">
				<Skeleton className="h-4 w-28" />
				<Skeleton className="h-3 w-64 mt-1" />
			</CardHeader>
			<CardContent className="py-6 space-y-3">
				{[0, 1, 2].map((i) => (
					<Skeleton key={i} className="h-10 w-full" />
				))}
			</CardContent>
		</Card>
	</div>
);

export default TreasuryPage;
